import { useEffect, useRef, useState } from "react";
import { usePrefersReducedMotion } from "@/features/viewer/hooks/usePrefersReducedMotion";

// Scroll-reveal that ENHANCES an already-visible default (DESIGN.md reduced-motion
// rule + impeccable "reveal animations must enhance an already-visible default").
// The content is in the DOM and renderable without JS; this only adds a soft
// rise-and-fade for browsers that can do it. It NEVER gates visibility:
//   - reduced motion           → shown instantly, no transition
//   - no IntersectionObserver  → shown instantly (SSR / very old browser)
//   - observer never fires     → a failsafe timer shows it anyway
// so a headless render, a hidden tab, or a crawler can never ship a blank section.
export function useReveal<T extends HTMLElement = HTMLDivElement>(): {
  ref: React.RefObject<T | null>;
  revealed: boolean;
} {
  const reducedMotion = usePrefersReducedMotion();
  const ref = useRef<T>(null);
  const canAnimate = !reducedMotion && typeof IntersectionObserver !== "undefined";
  // Start hidden ONLY when we can animate and will observe; otherwise visible.
  const [revealed, setRevealed] = useState(!canAnimate);

  useEffect(() => {
    if (!canAnimate) {
      setRevealed(true);
      return;
    }
    const el = ref.current;
    if (!el) {
      setRevealed(true);
      return;
    }
    const reveal = () => setRevealed(true);
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          reveal();
          observer.disconnect();
        }
      },
      // Fire a touch before the element is fully on screen, so it's settled by
      // the time the reader reaches it.
      { rootMargin: "0px 0px -10% 0px", threshold: 0.15 },
    );
    observer.observe(el);
    // Failsafe: throttled (background tab) or non-firing (headless) observers
    // must still resolve to visible — never a permanent blank.
    const timer = window.setTimeout(reveal, 1200);
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [canAnimate]);

  return { ref, revealed };
}
